import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from './Layout';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
    Divider, IconButton, ListItemIcon, Menu, MenuItem, Modal, Paper, Stack,
    Step, StepLabel, Stepper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip,
    Typography, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import DrawIcon from '@mui/icons-material/Draw';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IosShareIcon from '@mui/icons-material/IosShare';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PublicIcon from '@mui/icons-material/Public';
import GavelIcon from '@mui/icons-material/Gavel';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import QuoteService from '../services/QuoteService';
import CustomerService from '../services/CustomerService';
import ServiceService from '../services/ServiceService';
import ShareQuoteModal from './sub-components/ShareQuoteModal';
import ConfirmModal from './modal/ConfirmModal';
import GenerateContractModal from './modal/GenerateContractModal';
import { useToast } from '../contexts/ToastContext';
import styles from './css/Quotes.module.css';
import { useTranslation } from 'react-i18next';

const underlineStyle = {
    '& .MuiInput-underline:before': { borderBottomColor: '#dce3ea' },
    '& .MuiInput-underline:hover:before': { borderBottomColor: '#427898' },
    '& .MuiInput-underline:after': { borderBottomColor: '#427898' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#427898' },
};

const quoteStatusOptions = [
    { code: 'DRAFT',                       color: '#9E9E9E' },
    { code: 'SENT',                        color: '#4A90E2' },
    { code: 'AWAITING_SIGNATURE',          color: '#F4A261' },
    { code: 'AWAITING_COMPANY_SIGNATURE',  color: '#F4A261' },
    { code: 'COMPANY_SIGNED',              color: '#7B8FA1' },
    { code: 'AWAITING_CUSTOMER_SIGNATURE', color: '#F4A261' },
    { code: 'SIGNED',                      color: '#2EC4B6' },
    { code: 'ACCEPTED',                    color: '#7DCFB6' },
    { code: 'REJECTED',                    color: '#E07A5F' },
    { code: 'CANCELLED',                   color: '#B0B0B0' },
    { code: 'EXPIRED',                     color: '#F4C067' },
];

const statusColor = (code) => quoteStatusOptions.find(s => s.code === code)?.color || '#9E9E9E';
const fmtAmount = (v) => v != null ? `CHF ${Number(v).toFixed(2)}` : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('de-CH') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const emptyItemForm = { description: '', quantity: 1, unitPrice: '', mode: 'free', subServiceId: null };

const SideLabel = ({ children }) => (
    <Typography sx={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, mb: 0.4 }}>
        {children}
    </Typography>
);

const SideValue = ({ children, sx }) => (
    <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B', ...sx }}>
        {children}
    </Typography>
);

export default function QuoteInfo() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [quote, setQuote] = useState(null);
    const [items, setItems] = useState([]);
    const [contacts, setContacts] = useState([]);

    // Edit quote modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [customerContacts, setCustomerContacts] = useState([]);
    const [editSelectedCustomer, setEditSelectedCustomer] = useState(null);
    const [editSelectedContact, setEditSelectedContact] = useState(null);
    const [editForm, setEditForm] = useState({});

    // Item modal
    const [itemModalOpen, setItemModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemForm, setItemForm] = useState(emptyItemForm);

    // Share modal
    const [shareOpen, setShareOpen] = useState(false);

    // Overflow menu
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const closeMenu = () => setMenuAnchorEl(null);

    // Confirm modals
    const [confirmDeleteQuoteOpen, setConfirmDeleteQuoteOpen] = useState(false);
    const [confirmDeleteItemOpen, setConfirmDeleteItemOpen] = useState(false);
    const [confirmSentOpen, setConfirmSentOpen] = useState(false);
    const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
    const [confirmRejectOpen, setConfirmRejectOpen] = useState(false);
    const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
    const [deletingItemId, setDeletingItemId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Service catalog for item modal
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedSubService, setSelectedSubService] = useState(null);

    // Signing
    const [startingSign, setStartingSign] = useState(false);
    const [requestingCustomer, setRequestingCustomer] = useState(false);
    const [companySigningUrl, setCompanySigningUrl] = useState(null);

    // Portal publish
    const [publishing, setPublishing] = useState(false);

    // Contract generation
    const [generatingContract, setGeneratingContract] = useState(false);

    // PDF preview
    const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    // Notes
    const [notes, setNotes] = useState([]);
    const [newNoteBody, setNewNoteBody] = useState('');
    const [editingNoteId, setEditingNoteId] = useState(null);
    const [editingNoteBody, setEditingNoteBody] = useState('');
    const [confirmDeleteNoteOpen, setConfirmDeleteNoteOpen] = useState(false);
    const [deletingNoteId, setDeletingNoteId] = useState(null);
    const [noteSubmitting, setNoteSubmitting] = useState(false);

    const isDraft = quote?.status === 'DRAFT';
    const isEditable = isDraft || quote?.status === 'CHANGES_REQUESTED';
    const isSent = quote?.status === 'SENT';
    const isSigned = quote?.status === 'SIGNED';
    const isTerminal = ['CANCELLED', 'REJECTED', 'EXPIRED'].includes(quote?.status);
    const isCancellable = ['DRAFT', 'SENT', 'AWAITING_COMPANY_SIGNATURE', 'COMPANY_SIGNED', 'AWAITING_CUSTOMER_SIGNATURE'].includes(quote?.status);
    const isRejectable = ['SENT', 'AWAITING_COMPANY_SIGNATURE', 'COMPANY_SIGNED', 'AWAITING_CUSTOMER_SIGNATURE', 'SIGNED'].includes(quote?.status);

    const signingStep = {
        'DRAFT': 0, 'SENT': 0,
        'AWAITING_COMPANY_SIGNATURE': 1,
        'COMPANY_SIGNED': 2,
        'AWAITING_CUSTOMER_SIGNATURE': 2,
        'AWAITING_SIGNATURE': 2,
        'SIGNED': 3,
        'ACCEPTED': 3,
        'REJECTED': 3,
        'CANCELLED': 0,
        'EXPIRED': 0,
    }[quote?.status] ?? 0;

    // ── Load ──────────────────────────────────────────────────────────────────

    const loadQuote = async () => {
        setLoading(true);
        try {
            const data = await QuoteService.getQuote(id);
            setQuote(data.quote);
            setItems(data.items || []);
            setCompanySigningUrl(data.companySigningUrl || null);
        } catch {
            showToast(t('quotes.info.loadError'), 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadQuote(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadNotes = async () => {
        try {
            const data = await QuoteService.getNotes(id);
            setNotes(data || []);
        } catch {
            // non-critical
        }
    };

    useEffect(() => { loadNotes(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!quote?.customerId) return;
        CustomerService.getCustomer(quote.customerId).then(data => {
            setContacts(data.customerContact || []);
        }).catch(() => {});
    }, [quote?.customerId]);

    useEffect(() => {
        CustomerService.getCustomers(0, 200).then(d => setCustomers(d.content || [])).catch(() => {});
    }, []);

    useEffect(() => {
        ServiceService.getAllServices().then(d => setServices(d.content || d || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!editSelectedCustomer) { setCustomerContacts([]); return; }
        CustomerService.getCustomer(editSelectedCustomer.id).then(d =>
            setCustomerContacts(d.customerContact || [])
        ).catch(() => setCustomerContacts([]));
    }, [editSelectedCustomer]);

    // ── Edit Quote ────────────────────────────────────────────────────────────

    const openEditModal = () => {
        const cust = customers.find(c => c.id === quote.customerId) || null;
        setEditSelectedCustomer(cust);
        setEditForm({
            customerId: quote.customerId,
            customerContactId: quote.customerContactId || '',
            quoteDate: quote.quoteDate || '',
            validUntil: quote.validUntil || '',
            taxRate: quote.taxRate ?? 7.7,
            notes: quote.notes || '',
        });
        setEditModalOpen(true);
    };

    const handleEditSave = async () => {
        setSubmitting(true);
        try {
            await QuoteService.updateQuote(id, editForm);
            showToast(t('quotes.info.updated'), 'success');
            setEditModalOpen(false);
            loadQuote();
        } catch {
            showToast(t('quotes.info.saveError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Items ─────────────────────────────────────────────────────────────────

    const openAddItem = () => {
        setEditingItem(null);
        setItemForm(emptyItemForm);
        setSelectedService(null);
        setSelectedSubService(null);
        setItemModalOpen(true);
    };

    const openEditItem = (item) => {
        setEditingItem(item);
        const hasSS = !!item.subServiceId;
        setItemForm({
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            mode: hasSS ? 'catalog' : 'free',
            subServiceId: item.subServiceId || null,
        });
        if (hasSS) {
            const svc = services.find(s => s.subServices?.some(ss => ss.id === item.subServiceId));
            setSelectedService(svc || null);
            setSelectedSubService(svc?.subServices?.find(ss => ss.id === item.subServiceId) || null);
        } else {
            setSelectedService(null);
            setSelectedSubService(null);
        }
        setItemModalOpen(true);
    };

    const handleStartSigning = async () => {
        setStartingSign(true);
        try {
            await QuoteService.startSigning(id);
            await loadQuote();
        } catch {
            showToast(t('quotes.info.signingStartError'), 'error');
        } finally {
            setStartingSign(false);
        }
    };

    const handleRequestCustomerSigning = async () => {
        setRequestingCustomer(true);
        try {
            await QuoteService.requestCustomerSigning(id);
            showToast(t('quotes.info.customerSigRequestSent'), 'success');
            loadQuote();
        } catch {
            showToast(t('quotes.info.customerSigRequestError'), 'error');
        } finally {
            setRequestingCustomer(false);
        }
    };

    const handleItemSave = async () => {
        const payload = {
            description: itemForm.description,
            quantity: parseFloat(itemForm.quantity) || 1,
            unitPrice: parseFloat(itemForm.unitPrice) || 0,
            ...(itemForm.mode === 'catalog' && itemForm.subServiceId
                ? { subServiceId: itemForm.subServiceId } : {}),
        };
        setSubmitting(true);
        try {
            if (editingItem) {
                await QuoteService.updateItem(id, editingItem.id, payload);
            } else {
                await QuoteService.addItem(id, payload);
            }
            setItemModalOpen(false);
            loadQuote();
        } catch {
            showToast(t('quotes.info.itemSaveError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteItem = async () => {
        if (!deletingItemId) return;
        setSubmitting(true);
        try {
            await QuoteService.deleteItem(id, deletingItemId);
            setConfirmDeleteItemOpen(false);
            loadQuote();
        } catch {
            showToast(t('quotes.info.deleteError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Status Actions ────────────────────────────────────────────────────────

    const handleMarkSent = async () => {
        setSubmitting(true);
        try {
            await QuoteService.changeStatus(id, 'SENT');
            showToast(t('quotes.info.markedSent'), 'success');
            setConfirmSentOpen(false);
            loadQuote();
        } catch {
            showToast(t('quotes.info.statusError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAccept = async () => {
        setSubmitting(true);
        try {
            await QuoteService.acceptQuote(id);
            showToast(t('quotes.info.accepted'), 'success');
            setConfirmAcceptOpen(false);
            loadQuote();
        } catch {
            showToast(t('quotes.info.acceptError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReject = async () => {
        setSubmitting(true);
        try {
            await QuoteService.rejectQuote(id);
            showToast(t('quotes.info.markedRejected'), 'info');
            setConfirmRejectOpen(false);
            loadQuote();
        } catch {
            showToast(t('quotes.info.error'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        setSubmitting(true);
        try {
            await QuoteService.cancelQuote(id);
            showToast(t('quotes.info.cancelled'), 'info');
            setConfirmCancelOpen(false);
            loadQuote();
        } catch {
            showToast(t('quotes.info.cancelError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePublishToPortal = async () => {
        setPublishing(true);
        try {
            await QuoteService.publishToPortal(id);
            showToast(t('quotes.info.publishedPortal'), 'success');
            loadQuote();
        } catch {
            showToast(t('quotes.info.publishPortalError'), 'error');
        } finally {
            setPublishing(false);
        }
    };

    const handleDuplicate = async () => {
        setSubmitting(true);
        try {
            const newQuote = await QuoteService.duplicateQuote(id);
            showToast(t('quotes.info.duplicated', { name: newQuote.displayName }), 'success');
            navigate(`/quote-info/${newQuote.id}`);
        } catch {
            showToast(t('quotes.info.duplicateError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const handleContractGenerated = (contract) => {
        setGeneratingContract(false);
        showToast(t('quotes.info.contractCreated', { name: contract.displayName }), 'success');
        navigate(`/contracts/${contract.id}`);
    };

    const handleDeleteQuote = async () => {
        setSubmitting(true);
        try {
            await QuoteService.deleteQuote(id);
            showToast(t('quotes.info.quoteDeleted'), 'success');
            navigate('/quotes');
        } catch {
            showToast(t('quotes.info.deleteError'), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // ── Notes ─────────────────────────────────────────────────────────────────

    const handleAddNote = async () => {
        if (!newNoteBody.trim()) return;
        setNoteSubmitting(true);
        try {
            await QuoteService.addNote(id, newNoteBody);
            setNewNoteBody('');
            loadNotes();
        } catch {
            showToast(t('quotes.info.noteSaveError'), 'error');
        } finally {
            setNoteSubmitting(false);
        }
    };

    const handleUpdateNote = async () => {
        if (!editingNoteBody.trim()) return;
        setNoteSubmitting(true);
        try {
            await QuoteService.updateNote(id, editingNoteId, editingNoteBody);
            setEditingNoteId(null);
            loadNotes();
        } catch {
            showToast(t('quotes.info.noteSaveError'), 'error');
        } finally {
            setNoteSubmitting(false);
        }
    };

    const handleDeleteNote = async () => {
        if (!deletingNoteId) return;
        setNoteSubmitting(true);
        try {
            await QuoteService.deleteNote(id, deletingNoteId);
            setConfirmDeleteNoteOpen(false);
            loadNotes();
        } catch {
            showToast(t('quotes.info.deleteError'), 'error');
        } finally {
            setNoteSubmitting(false);
        }
    };

    // ── PDF ───────────────────────────────────────────────────────────────────

    const openPdfPreview = async () => {
        setPdfLoading(true);
        setPdfPreviewOpen(true);
        try {
            const response = await QuoteService.downloadQuote(id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            setPdfBlobUrl(URL.createObjectURL(blob));
        } catch {
            showToast(t('quotes.info.pdfLoadError'), 'error');
            setPdfPreviewOpen(false);
        } finally {
            setPdfLoading(false);
        }
    };

    const closePdfPreview = () => {
        setPdfPreviewOpen(false);
        if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
        }
    };

    const downloadPdf = async () => {
        try {
            const response = await QuoteService.downloadQuote(id);
            const cd = response.headers['content-disposition'] || '';
            const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)/i);
            const filename = match ? decodeURIComponent(match[1]) : `${quote.displayName}.pdf`;
            const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } catch {
            showToast(t('quotes.info.pdfDownloadError'), 'error');
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Layout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            </Layout>
        );
    }

    if (!quote) {
        return (
            <Layout>
                <Box sx={{ p: 4 }}>
                    <Typography>{t('quotes.info.notFound')}</Typography>
                    <Button onClick={() => navigate('/quotes')} sx={{ mt: 2 }}>{t('quotes.info.back')}</Button>
                </Box>
            </Layout>
        );
    }

    const computedTotal = (itemForm.quantity || 0) * (itemForm.unitPrice || 0);
    const color = statusColor(quote.status);

    return (
        <Layout fullBleed>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

                {/* ── Page header ── */}
                <Box sx={{
                    bgcolor: '#fff',
                    borderBottom: '1px solid #EEF2F6',
                    px: 3,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                }}>
                    {/* Breadcrumb */}
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                        onClick={() => navigate('/quotes')}
                        sx={{
                            color: '#64748B', fontWeight: 500, textTransform: 'none',
                            fontSize: 13, px: 1, minWidth: 0,
                            '&:hover': { bgcolor: '#F1F5F9' },
                        }}
                    >
                        {t('quotes.info.back')}
                    </Button>

                    <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />

                    {/* Title + chips */}
                    <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A', whiteSpace: 'nowrap' }}>
                        {quote.displayName}
                    </Typography>
                    <Chip
                        label={t(`quotes.statuses.${quote.status}`, { defaultValue: quote.status })}
                        size="small"
                        sx={{
                            bgcolor: color + '18',
                            color,
                            fontWeight: 600,
                            fontSize: 11,
                            border: `1px solid ${color}30`,
                            height: 22,
                        }}
                    />
                    {quote.publishedToPortal && (
                        <Chip
                            label={t('quotes.info.publishedChip')}
                            size="small"
                            sx={{ bgcolor: '#0D948820', color: '#0D9488', fontWeight: 600, fontSize: 11, height: 22 }}
                        />
                    )}

                    <Box sx={{ flex: 1 }} />

                    {/* Header actions */}
                    <Stack direction="row" spacing={0.5} alignItems="center">

                        {/* Edit (icon only) */}
                        {isEditable && (
                            <Tooltip title={t('quotes.edit')}>
                                <IconButton size="small" onClick={openEditModal}
                                    sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                                    <EditIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}

                        {/* Download PDF (icon only) */}
                        <Tooltip title={t('quotes.info.pdfDownload')}>
                            <IconButton size="small" onClick={downloadPdf}
                                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                                <DownloadIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>

                        {/* Overflow menu */}
                        <Tooltip title="More actions">
                            <IconButton
                                size="small"
                                onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                                sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}
                            >
                                <MoreVertIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Tooltip>

                        <Menu
                            anchorEl={menuAnchorEl}
                            open={Boolean(menuAnchorEl)}
                            onClose={closeMenu}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    borderRadius: 2, minWidth: 210,
                                    border: '1px solid #E8EDF2',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                                    mt: 0.5,
                                },
                            }}
                        >
                            <MenuItem onClick={() => { openPdfPreview(); closeMenu(); }} dense>
                                <ListItemIcon><PictureAsPdfIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                                {t('quotes.info.pdfPreview')}
                            </MenuItem>
                            <MenuItem onClick={() => { downloadPdf(); closeMenu(); }} dense>
                                <ListItemIcon><DownloadIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                                {t('quotes.info.pdfDownload')}
                            </MenuItem>
                            <MenuItem onClick={() => { setShareOpen(true); closeMenu(); }} dense>
                                <ListItemIcon><IosShareIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                                {t('quotes.share')}
                            </MenuItem>

                            {isEditable && <Divider sx={{ my: 0.5 }} />}
                            {isEditable && (
                                <MenuItem onClick={() => { handlePublishToPortal(); closeMenu(); }} disabled={publishing} dense>
                                    <ListItemIcon><PublicIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                                    {quote?.status === 'CHANGES_REQUESTED' ? t('quotes.info.republishPortal') : t('quotes.info.publishPortal')}
                                </MenuItem>
                            )}

                            {isDraft && <Divider sx={{ my: 0.5 }} />}
                            {isDraft && (
                                <MenuItem onClick={() => { setConfirmSentOpen(true); closeMenu(); }} dense>
                                    <ListItemIcon><ArrowForwardIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                                    {t('quotes.markAsSent')}
                                </MenuItem>
                            )}

                            {(isDraft || isSent || isSigned) && <Divider sx={{ my: 0.5 }} />}
                            {(isDraft || isSent || isSigned) && (
                                <MenuItem onClick={() => { setConfirmAcceptOpen(true); closeMenu(); }} dense>
                                    <ListItemIcon><CheckIcon fontSize="small" sx={{ color: '#2EC4B6' }} /></ListItemIcon>
                                    {t('quotes.info.markAccepted')}
                                </MenuItem>
                            )}
                            {isRejectable && (
                                <MenuItem onClick={() => { setConfirmRejectOpen(true); closeMenu(); }} dense>
                                    <ListItemIcon><BlockIcon fontSize="small" sx={{ color: '#E07A5F' }} /></ListItemIcon>
                                    {t('quotes.info.markRejected')}
                                </MenuItem>
                            )}

                            {(quote?.status === 'CANCELLED' || quote?.status === 'REJECTED') && (
                                <MenuItem onClick={() => { handleDuplicate(); closeMenu(); }} disabled={submitting} dense>
                                    <ListItemIcon><ContentCopyIcon fontSize="small" sx={{ color: '#64748B' }} /></ListItemIcon>
                                    {t('quotes.info.duplicate')}
                                </MenuItem>
                            )}

                            {(isCancellable || !isTerminal) && <Divider sx={{ my: 0.5 }} />}
                            {isCancellable && (
                                <MenuItem onClick={() => { setConfirmCancelOpen(true); closeMenu(); }} dense sx={{ color: '#B45309' }}>
                                    <ListItemIcon><BlockIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
                                    {t('quotes.info.cancel')}
                                </MenuItem>
                            )}
                            {!isTerminal && (
                                <MenuItem onClick={() => { setConfirmDeleteQuoteOpen(true); closeMenu(); }} dense sx={{ color: '#EF4444' }}>
                                    <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: 'inherit' }} /></ListItemIcon>
                                    {t('quotes.info.delete')}
                                </MenuItem>
                            )}
                        </Menu>
                    </Stack>
                </Box>

                {/* ── Body ── */}
                <Box sx={{
                    display: 'flex',
                    gap: 3,
                    p: { xs: 2, md: 3 },
                    maxWidth: 1280,
                    mx: 'auto',
                    alignItems: 'flex-start',
                    flexDirection: { xs: 'column-reverse', md: 'row' },
                }}>

                    {/* ── Main / Left column ── */}
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                        {/* How to proceed */}
                        {['ACCEPTED', 'SIGNED'].includes(quote?.status) && !quote.linkedProjectId && !quote.contractId && (
                            <Paper elevation={0} sx={{ border: '1.5px solid #427898', borderRadius: 2, p: 3, bgcolor: '#EFF6FF' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#427898', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                                    {t('quotes.info.howToProceed')}
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <Box
                                        onClick={() => setGeneratingContract(true)}
                                        sx={{
                                            flex: 1, p: 2.5, borderRadius: 1.5, cursor: 'pointer',
                                            border: '1px solid #CBD5E1', bgcolor: '#fff',
                                            transition: 'all 0.15s',
                                            '&:hover': { borderColor: '#427898', boxShadow: '0 2px 12px rgba(66,120,152,0.12)' },
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                            <GavelIcon sx={{ fontSize: 18, color: '#427898' }} />
                                            <Typography fontWeight={700} sx={{ fontSize: 14, color: '#0F172A' }}>
                                                {t('quotes.info.createContract')}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                                            {t('quotes.info.createContractDesc')}
                                        </Typography>
                                    </Box>
                                    <Box
                                        onClick={() => navigate('/create-project')}
                                        sx={{
                                            flex: 1, p: 2.5, borderRadius: 1.5, cursor: 'pointer',
                                            border: '1px solid #CBD5E1', bgcolor: '#fff',
                                            transition: 'all 0.15s',
                                            '&:hover': { borderColor: '#427898', boxShadow: '0 2px 12px rgba(66,120,152,0.12)' },
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                            <FolderOpenIcon sx={{ fontSize: 18, color: '#427898' }} />
                                            <Typography fontWeight={700} sx={{ fontSize: 14, color: '#0F172A' }}>
                                                {t('quotes.info.directProject')}
                                            </Typography>
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                                            {t('quotes.info.directProjectDesc')}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>
                        )}

                        {/* Signing stepper */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2.5 }}>
                                    {t('quotes.info.signingProcess')}
                                </Typography>
                                <Stepper activeStep={signingStep} alternativeLabel>
                                    {[t('quotes.info.stepCreated'), t('quotes.info.stepCompany'), t('quotes.info.stepCustomer'), t('quotes.info.stepDone')].map((label, i) => (
                                        <Step key={label} completed={signingStep > i}>
                                            <StepLabel
                                                StepIconProps={{
                                                    sx: {
                                                        '&.Mui-active': { color: '#427898' },
                                                        '&.Mui-completed': { color: '#2EC4B6' },
                                                    }
                                                }}
                                            >
                                                <Typography variant="caption" sx={{ fontSize: 11 }}>{label}</Typography>
                                            </StepLabel>
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>

                            {/* Contextual signing actions */}
                            {(isDraft || isSent) && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="info" sx={{ borderRadius: 1.5, mb: 1.5 }}>
                                        {t('quotes.info.readyToSign')}
                                    </Alert>
                                    <Button
                                        variant="contained"
                                        startIcon={<DrawIcon />}
                                        onClick={handleStartSigning}
                                        disabled={startingSign}
                                        sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                                    >
                                        {startingSign
                                            ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />{t('quotes.info.openingSkribble')}</>
                                            : t('quotes.info.startSigningProcess')}
                                    </Button>
                                </Box>
                            )}

                            {quote?.status === 'AWAITING_COMPANY_SIGNATURE' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert
                                        severity="info"
                                        action={
                                            <Button size="small" color="inherit" onClick={loadQuote} sx={{ fontWeight: 600, textTransform: 'none' }}>
                                                {t('quotes.info.refreshStatus')}
                                            </Button>
                                        }
                                        sx={{ borderRadius: 1.5, mb: companySigningUrl ? 1.5 : 0 }}
                                    >
                                        {t('quotes.info.awaitingCompanyInfo')}
                                    </Alert>
                                    {companySigningUrl && (
                                        <Button
                                            variant="contained"
                                            startIcon={<DrawIcon />}
                                            onClick={() => window.open(companySigningUrl, '_blank', 'noopener,noreferrer')}
                                            sx={{ bgcolor: '#F4A261', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#e8955a', boxShadow: 'none' } }}
                                        >
                                            {t('quotes.info.openSkribble')}
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {quote?.status === 'COMPANY_SIGNED' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="success" sx={{ borderRadius: 1.5, mb: 1.5 }}>
                                        {t('quotes.info.companySigned')}
                                    </Alert>
                                    <Button
                                        variant="contained"
                                        startIcon={<DrawIcon />}
                                        disabled={requestingCustomer}
                                        onClick={handleRequestCustomerSigning}
                                        sx={{ bgcolor: '#427898', textTransform: 'none', fontWeight: 600, borderRadius: 1.5, boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}
                                    >
                                        {requestingCustomer
                                            ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />{t('quotes.info.requestingCustomer')}</>
                                            : t('quotes.info.requestCustomerSig')}
                                    </Button>
                                </Box>
                            )}

                            {(quote?.status === 'AWAITING_CUSTOMER_SIGNATURE' || quote?.status === 'AWAITING_SIGNATURE') && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="info" sx={{ borderRadius: 1.5 }}>
                                        {t('quotes.info.awaitingCustomerInfo', { contact: quote.customerContactName || t('quotes.info.contact') })}
                                    </Alert>
                                </Box>
                            )}

                            {quote?.status === 'SIGNED' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert severity="success" sx={{ borderRadius: 1.5 }}>{t('quotes.info.signedInfo')}</Alert>
                                </Box>
                            )}
                            {quote?.status === 'CANCELLED' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert
                                        severity="warning"
                                        action={
                                            <Button size="small" color="inherit" startIcon={<ContentCopyIcon />} onClick={handleDuplicate} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                                {t('quotes.info.duplicate')}
                                            </Button>
                                        }
                                        sx={{ borderRadius: 1.5 }}
                                    >
                                        {t('quotes.info.cancelledInfo')}
                                    </Alert>
                                </Box>
                            )}
                            {quote?.status === 'REJECTED' && (
                                <Box sx={{ px: 3, pb: 2.5 }}>
                                    <Alert
                                        severity="error"
                                        action={
                                            <Button size="small" color="inherit" startIcon={<ContentCopyIcon />} onClick={handleDuplicate} disabled={submitting} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                                {t('quotes.info.duplicate')}
                                            </Button>
                                        }
                                        sx={{ borderRadius: 1.5 }}
                                    >
                                        {t('quotes.info.rejectedInfo')}
                                    </Alert>
                                </Box>
                            )}
                        </Paper>

                        {/* Line items */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{
                                px: 3, py: 2,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                borderBottom: '1px solid #F1F5F9',
                            }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
                                    {t('quotes.info.positions')}
                                    {items.length > 0 && (
                                        <Typography component="span" sx={{ ml: 1, fontSize: 12, color: '#94A3B8', fontWeight: 400 }}>
                                            ({items.length})
                                        </Typography>
                                    )}
                                </Typography>
                                {isEditable && (
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                                        onClick={openAddItem}
                                        sx={{
                                            textTransform: 'none', fontWeight: 600, fontSize: 13,
                                            color: '#427898', px: 1.5,
                                            '&:hover': { bgcolor: '#F0F7FF' },
                                        }}
                                    >
                                        {t('quotes.info.addPosition')}
                                    </Button>
                                )}
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#FAFBFC' }}>
                                            {['posNumCol', 'descriptionCol', 'quantityCol', 'unitPriceCol', 'totalPriceCol'].map((key, i) => (
                                                <TableCell
                                                    key={key}
                                                    align={i >= 2 ? 'right' : 'left'}
                                                    sx={{ color: '#94A3B8', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.5 }}
                                                >
                                                    {t(`quotes.info.${key}`)}
                                                </TableCell>
                                            ))}
                                            {isEditable && <TableCell sx={{ width: 64 }} />}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={isEditable ? 6 : 5} align="center" sx={{ py: 6 }}>
                                                    <Typography sx={{ color: '#CBD5E1', fontSize: 14 }}>
                                                        {t('quotes.info.noPositions')}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : items.map((item, idx) => (
                                            <TableRow
                                                key={item.id}
                                                sx={{
                                                    '&:hover': { bgcolor: '#FAFBFC' },
                                                    '&:hover .row-actions': { opacity: 1 },
                                                    '& .row-actions': { opacity: 0, transition: 'opacity 0.15s' },
                                                }}
                                            >
                                                <TableCell sx={{ color: '#94A3B8', fontSize: 13, width: 48 }}>{idx + 1}</TableCell>
                                                <TableCell sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B' }}>{item.description}</TableCell>
                                                <TableCell align="right" sx={{ fontSize: 13, color: '#475569' }}>{item.quantity}</TableCell>
                                                <TableCell align="right" sx={{ fontSize: 13, color: '#475569' }}>{fmtAmount(item.unitPrice)}</TableCell>
                                                <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600, color: '#0F172A' }}>{fmtAmount(item.totalPrice)}</TableCell>
                                                {isEditable && (
                                                    <TableCell align="right">
                                                        <Stack direction="row" className="row-actions" justifyContent="flex-end">
                                                            <IconButton size="small" onClick={() => openEditItem(item)}
                                                                sx={{ color: '#94A3B8', '&:hover': { color: '#427898', bgcolor: '#F0F7FF' } }}>
                                                                <EditIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                            <IconButton size="small"
                                                                onClick={() => { setDeletingItemId(item.id); setConfirmDeleteItemOpen(true); }}
                                                                sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                                                                <DeleteIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Stack>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {items.length > 0 && (
                                <Box sx={{ px: 3, py: 2.5, borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'flex-end' }}>
                                    <Stack spacing={0.75} sx={{ minWidth: 248 }}>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>{t('quotes.info.subtotal')}</Typography>
                                            <Typography sx={{ fontSize: 13, color: '#475569' }}>{fmtAmount(quote.totalBeforeTax)}</Typography>
                                        </Stack>
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>{t('quotes.info.taxWithRate', { rate: quote.taxRate ?? 0 })}</Typography>
                                            <Typography sx={{ fontSize: 13, color: '#475569' }}>{fmtAmount(quote.taxAmount)}</Typography>
                                        </Stack>
                                        <Divider sx={{ my: 0.25 }} />
                                        <Stack direction="row" justifyContent="space-between">
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>{t('quotes.info.total')}</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#427898' }}>{fmtAmount(quote.totalAmount)}</Typography>
                                        </Stack>
                                    </Stack>
                                </Box>
                            )}
                        </Paper>

                        {/* Notes */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F1F5F9' }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>
                                    {t('quotes.info.notes')}
                                    {notes.length > 0 && (
                                        <Typography component="span" sx={{ ml: 1, fontSize: 12, color: '#94A3B8', fontWeight: 400 }}>
                                            ({notes.length})
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>

                            <Box sx={{ p: 3 }}>
                                {notes.length === 0 && (
                                    <Typography sx={{ color: '#CBD5E1', fontSize: 13, mb: 2 }}>
                                        {t('quotes.info.noNotes')}
                                    </Typography>
                                )}

                                <Stack spacing={1.5} sx={{ mb: notes.length > 0 ? 2.5 : 0 }}>
                                    {notes.map(note => {
                                        const isChangeRequest = note.noteType === 'CHANGE_REQUEST';
                                        const isCustomer = note.source === 'CUSTOMER';
                                        return (
                                            <Box
                                                key={note.id}
                                                sx={{
                                                    borderRadius: 1.5,
                                                    p: 2,
                                                    bgcolor: isChangeRequest ? '#FFFBEB' : '#F8FAFC',
                                                    border: `1px solid ${isChangeRequest ? '#FDE68A' : '#EEF2F6'}`,
                                                    borderLeft: `3px solid ${isChangeRequest ? '#F4A261' : '#427898'}`,
                                                }}
                                            >
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        {isChangeRequest && (
                                                            <Chip
                                                                label={t('quotes.info.changeRequestChip')}
                                                                size="small"
                                                                sx={{ bgcolor: '#F4A261', color: '#fff', fontWeight: 600, fontSize: 10, height: 18 }}
                                                            />
                                                        )}
                                                        <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>
                                                            {isCustomer ? t('quotes.info.customer_source') : note.authorName}
                                                            {' · '}{fmtDateTime(note.createdAt)}
                                                            {(new Date(note.updatedAt) - new Date(note.createdAt)) > 1000 && (
                                                                <Typography component="span" sx={{ fontSize: 11, color: '#CBD5E1' }}>
                                                                    {' '}{t('quotes.info.edited')}
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    </Stack>
                                                    <Stack direction="row" spacing={0}>
                                                        {note.canEdit && editingNoteId !== note.id && (
                                                            <IconButton size="small"
                                                                onClick={() => { setEditingNoteId(note.id); setEditingNoteBody(note.body); }}
                                                                sx={{ color: '#CBD5E1', '&:hover': { color: '#427898' } }}>
                                                                <EditIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        )}
                                                        {note.canDelete && (
                                                            <IconButton size="small"
                                                                onClick={() => { setDeletingNoteId(note.id); setConfirmDeleteNoteOpen(true); }}
                                                                sx={{ color: '#CBD5E1', '&:hover': { color: '#EF4444' } }}>
                                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        )}
                                                    </Stack>
                                                </Stack>

                                                {editingNoteId === note.id ? (
                                                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                                                        <TextField
                                                            multiline minRows={2} fullWidth size="small"
                                                            value={editingNoteBody}
                                                            onChange={e => setEditingNoteBody(e.target.value)}
                                                            variant="outlined"
                                                        />
                                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                                            <Button size="small" sx={{ textTransform: 'none', color: '#64748B' }} onClick={() => setEditingNoteId(null)}>
                                                                {t('common.cancel')}
                                                            </Button>
                                                            <Button size="small" variant="contained" disabled={noteSubmitting || !editingNoteBody.trim()}
                                                                onClick={handleUpdateNote}
                                                                sx={{ bgcolor: '#427898', textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#355f77', boxShadow: 'none' } }}>
                                                                {t('common.save')}
                                                            </Button>
                                                        </Stack>
                                                    </Stack>
                                                ) : (
                                                    <Typography sx={{ fontSize: 13, color: '#334155', mt: 0.75, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                                        {note.body}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Stack>

                                {/* Add note */}
                                <Stack spacing={1.5}>
                                    <TextField
                                        multiline minRows={2} fullWidth size="small"
                                        placeholder={t('quotes.info.newNotePlaceholder')}
                                        value={newNoteBody}
                                        onChange={e => setNewNoteBody(e.target.value)}
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 1.5,
                                                fontSize: 13,
                                                '&:hover fieldset': { borderColor: '#427898' },
                                                '&.Mui-focused fieldset': { borderColor: '#427898' },
                                            }
                                        }}
                                    />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={<AddIcon sx={{ fontSize: '15px !important' }} />}
                                            disabled={noteSubmitting || !newNoteBody.trim()}
                                            onClick={handleAddNote}
                                            sx={{
                                                bgcolor: '#427898', textTransform: 'none', fontWeight: 600,
                                                borderRadius: 1.5, boxShadow: 'none',
                                                '&:hover': { bgcolor: '#355f77', boxShadow: 'none' },
                                            }}
                                        >
                                            {t('quotes.info.addNote')}
                                        </Button>
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>
                    </Box>

                    {/* ── Right sidebar ── */}
                    <Box sx={{
                        width: { xs: '100%', md: 288 },
                        flexShrink: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        position: { md: 'sticky' },
                        top: { md: 72 },
                    }}>

                        {/* Customer */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F1F5F9' }}>
                                <SideLabel>{t('quotes.info.customer')}</SideLabel>
                            </Box>
                            <Box sx={{ p: 2.5 }}>
                                <Typography
                                    sx={{
                                        fontSize: 14, fontWeight: 600,
                                        color: quote.customerId ? '#427898' : '#0F172A',
                                        cursor: quote.customerId ? 'pointer' : 'default',
                                        '&:hover': quote.customerId ? { textDecoration: 'underline' } : {},
                                    }}
                                    onClick={() => quote.customerId && navigate(`/customers/${quote.customerId}`)}
                                >
                                    {quote.customerName}
                                </Typography>
                                {quote.customerContactName && (
                                    <Typography sx={{ mt: 0.5, fontSize: 13, color: '#64748B' }}>
                                        {quote.customerContactName}
                                    </Typography>
                                )}
                            </Box>
                        </Paper>

                        {/* Dates & settings */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F1F5F9' }}>
                                <SideLabel>Details</SideLabel>
                            </Box>
                            <Stack divider={<Divider sx={{ mx: 2.5 }} />}>
                                <Box sx={{ px: 2.5, py: 1.75 }}>
                                    <SideLabel>{t('quotes.info.quoteDate')}</SideLabel>
                                    <SideValue>{fmtDate(quote.quoteDate)}</SideValue>
                                </Box>
                                <Box sx={{ px: 2.5, py: 1.75 }}>
                                    <SideLabel>{t('quotes.info.validUntil')}</SideLabel>
                                    <SideValue>{fmtDate(quote.validUntil)}</SideValue>
                                </Box>
                                <Box sx={{ px: 2.5, py: 1.75 }}>
                                    <SideLabel>{t('quotes.info.taxRate')}</SideLabel>
                                    <SideValue>{quote.taxRate ?? 0}%</SideValue>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Financials */}
                        <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F1F5F9' }}>
                                <SideLabel>Financials</SideLabel>
                            </Box>
                            <Box sx={{ px: 2.5, py: 2 }}>
                                <Stack spacing={0.75}>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{t('quotes.info.subtotal')}</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#475569' }}>{fmtAmount(quote.totalBeforeTax)}</Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{t('quotes.info.taxWithRate', { rate: quote.taxRate ?? 0 })}</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#475569' }}>{fmtAmount(quote.taxAmount)}</Typography>
                                    </Stack>
                                    <Divider sx={{ my: 0.5 }} />
                                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>{t('quotes.info.total')}</Typography>
                                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#427898' }}>{fmtAmount(quote.totalAmount)}</Typography>
                                    </Stack>
                                </Stack>
                            </Box>
                        </Paper>

                        {/* Remarks */}
                        {quote.notes && (
                            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F1F5F9' }}>
                                    <SideLabel>{t('quotes.info.remarks')}</SideLabel>
                                </Box>
                                <Box sx={{ px: 2.5, py: 2 }}>
                                    <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{quote.notes}</Typography>
                                </Box>
                            </Paper>
                        )}

                        {/* Related */}
                        {(quote.linkedProjectId || quote.contractId) && (
                            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
                                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F1F5F9' }}>
                                    <SideLabel>Linked</SideLabel>
                                </Box>
                                <Stack sx={{ p: 1 }}>
                                    {quote.contractId && (
                                        <Box
                                            onClick={() => navigate(`/contracts/${quote.contractId}`)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 1.5, py: 1.25, borderRadius: 1.5, cursor: 'pointer',
                                                '&:hover': { bgcolor: '#F8FAFC' },
                                            }}
                                        >
                                            <GavelIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#427898', flex: 1 }}>
                                                {t('quotes.info.goToContract')}
                                            </Typography>
                                            <ArrowForwardIcon sx={{ fontSize: 14, color: '#CBD5E1' }} />
                                        </Box>
                                    )}
                                    {quote.linkedProjectId && (
                                        <Box
                                            onClick={() => navigate(`/project-info/${quote.linkedProjectId}`)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.5,
                                                px: 1.5, py: 1.25, borderRadius: 1.5, cursor: 'pointer',
                                                '&:hover': { bgcolor: '#F8FAFC' },
                                            }}
                                        >
                                            <FolderOpenIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                                            <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#427898', flex: 1 }}>
                                                {t('quotes.info.goToProject')}
                                            </Typography>
                                            <ArrowForwardIcon sx={{ fontSize: 14, color: '#CBD5E1' }} />
                                        </Box>
                                    )}
                                </Stack>
                            </Paper>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* ── Modals ── */}

            {/* Edit quote */}
            <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
                <Box className={styles.modalShell}>
                    <Box className={styles.modalHeader}>
                        <Typography variant="h6">{t('quotes.info.editTitle')}</Typography>
                        <IconButton onClick={() => setEditModalOpen(false)}><CloseIcon /></IconButton>
                    </Box>
                    <Box className={styles.modalContent}>
                        <Stack spacing={3}>
                            <Box className={styles.gridTwoCols}>
                                <Autocomplete
                                    options={customers}
                                    getOptionLabel={c => c.name || ''}
                                    value={editSelectedCustomer}
                                    onChange={(_, val) => {
                                        setEditSelectedCustomer(val);
                                        setEditForm(f => ({ ...f, customerId: val?.id || '' }));
                                    }}
                                    renderInput={p => <TextField {...p} label={`${t('quotes.info.customer')} *`} variant="standard" sx={underlineStyle} />}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                />
                                <Autocomplete
                                    options={customerContacts}
                                    getOptionLabel={c => `${c.firstName} ${c.lastName}`}
                                    value={customerContacts.find(c => c.id === editForm.customerContactId) || null}
                                    onChange={(_, val) => setEditForm(f => ({ ...f, customerContactId: val?.id || '' }))}
                                    disabled={!editSelectedCustomer}
                                    renderInput={p => <TextField {...p} label={t('quotes.info.contact')} variant="standard" sx={underlineStyle} />}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                />
                            </Box>
                            <Box className={styles.gridTwoCols}>
                                <TextField label={t('quotes.info.quoteDate')} type="date" value={editForm.quoteDate || ''}
                                    onChange={e => setEditForm(f => ({ ...f, quoteDate: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} variant="standard" sx={underlineStyle} fullWidth />
                                <TextField label={t('quotes.info.validUntil')} type="date" value={editForm.validUntil || ''}
                                    onChange={e => setEditForm(f => ({ ...f, validUntil: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} variant="standard" sx={underlineStyle} fullWidth />
                            </Box>
                            <TextField label={`${t('quotes.info.taxRate')} %`} type="number" value={editForm.taxRate ?? 7.7}
                                onChange={e => setEditForm(f => ({ ...f, taxRate: parseFloat(e.target.value) || 0 }))}
                                variant="standard" sx={{ ...underlineStyle, maxWidth: 200 }} inputProps={{ step: 0.1, min: 0 }} />
                            <TextField label={t('quotes.info.remarks')} multiline minRows={3} value={editForm.notes || ''}
                                onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                                variant="standard" sx={underlineStyle} fullWidth />
                            <Divider />
                            <Stack direction="row" justifyContent="flex-end" spacing={2}>
                                <Button onClick={() => setEditModalOpen(false)}>{t('common.cancel')}</Button>
                                <Button variant="contained" sx={{ bgcolor: '#427898', boxShadow: 'none' }} onClick={handleEditSave} disabled={submitting}>
                                    {t('common.save')}
                                </Button>
                            </Stack>
                        </Stack>
                    </Box>
                </Box>
            </Modal>

            {/* Add/Edit item */}
            <Modal open={itemModalOpen} onClose={() => setItemModalOpen(false)}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(92vw, 520px)', bgcolor: '#fff',
                    borderRadius: 2, boxShadow: '0 16px 48px rgba(0,0,0,0.14)', p: 4,
                }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 700 }}>
                            {editingItem ? t('quotes.info.editItemTitle') : t('quotes.info.addItemTitle')}
                        </Typography>
                        <IconButton onClick={() => setItemModalOpen(false)} size="small"><CloseIcon /></IconButton>
                    </Stack>
                    <Stack spacing={2}>
                        <ToggleButtonGroup
                            value={itemForm.mode}
                            exclusive
                            size="small"
                            onChange={(_, val) => {
                                if (!val) return;
                                setItemForm(f => ({ ...f, mode: val, subServiceId: null,
                                    description: val === 'free' ? f.description : '',
                                    unitPrice: val === 'free' ? f.unitPrice : '',
                                }));
                                setSelectedService(null);
                                setSelectedSubService(null);
                            }}
                        >
                            <ToggleButton value="free">{t('quotes.info.modeManual')}</ToggleButton>
                            <ToggleButton value="catalog">{t('quotes.info.modeCatalog')}</ToggleButton>
                        </ToggleButtonGroup>

                        {itemForm.mode === 'catalog' && (
                            <>
                                <Autocomplete
                                    options={services}
                                    getOptionLabel={s => s.name || ''}
                                    value={selectedService}
                                    onChange={(_, svc) => {
                                        setSelectedService(svc);
                                        setSelectedSubService(null);
                                        setItemForm(f => ({ ...f, subServiceId: null, description: '', unitPrice: '' }));
                                    }}
                                    renderInput={p => <TextField {...p} label={t('quotes.info.serviceLabel')} variant="standard" sx={underlineStyle} />}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                />
                                <Autocomplete
                                    options={selectedService?.subServices || []}
                                    getOptionLabel={ss => ss.name || ''}
                                    value={selectedSubService}
                                    disabled={!selectedService}
                                    onChange={(_, ss) => {
                                        setSelectedSubService(ss);
                                        if (ss) {
                                            setItemForm(f => ({
                                                ...f,
                                                subServiceId: ss.id,
                                                description: ss.name,
                                                unitPrice: ss.price ?? '',
                                            }));
                                        }
                                    }}
                                    renderOption={(props, ss) => (
                                        <li {...props} key={ss.id}>
                                            <Box>
                                                <Typography variant="body2">{ss.name}</Typography>
                                                {ss.unit && <Typography variant="caption" color="text.secondary">{ss.unit}</Typography>}
                                            </Box>
                                        </li>
                                    )}
                                    renderInput={p => <TextField {...p} label={t('quotes.info.subserviceLabel')} variant="standard" sx={underlineStyle} />}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                />
                                {selectedSubService?.unit && (
                                    <Typography variant="caption" color="text.secondary">
                                        {t('quotes.info.unitLabel')}: {selectedSubService.unit}
                                    </Typography>
                                )}
                            </>
                        )}

                        <TextField label={t('quotes.info.descriptionRequired')} value={itemForm.description}
                            onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))}
                            variant="standard" sx={underlineStyle} fullWidth />
                        <Box className={styles.gridTwoCols}>
                            <TextField label={t('quotes.info.quantityLabel')} type="number" value={itemForm.quantity}
                                onChange={e => setItemForm(f => ({ ...f, quantity: e.target.value }))}
                                variant="standard" sx={underlineStyle} inputProps={{ step: 0.01, min: 0 }} fullWidth />
                            <TextField label={t('quotes.info.unitPriceLabel')} type="number" value={itemForm.unitPrice}
                                onChange={e => setItemForm(f => ({ ...f, unitPrice: e.target.value }))}
                                variant="standard" sx={underlineStyle} inputProps={{ step: 0.01, min: 0 }} fullWidth />
                        </Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                                {t('quotes.info.totalPriceCalc')}: <strong>{fmtAmount(computedTotal)}</strong>
                            </Typography>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="flex-end" spacing={2}>
                            <Button onClick={() => setItemModalOpen(false)}>{t('common.cancel')}</Button>
                            <Button variant="contained" sx={{ bgcolor: '#427898', boxShadow: 'none' }} onClick={handleItemSave}
                                disabled={submitting || !itemForm.description}>
                                {editingItem ? t('common.save') : t('quotes.addItem')}
                            </Button>
                        </Stack>
                    </Stack>
                </Box>
            </Modal>

            {/* Share modal */}
            <ShareQuoteModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                quote={quote}
                contacts={contacts}
                onEmailSent={loadQuote}
            />

            {/* Delete quote */}
            <ConfirmModal open={confirmDeleteQuoteOpen} onClose={() => setConfirmDeleteQuoteOpen(false)}
                onConfirm={handleDeleteQuote} isLoading={submitting}
                question={t('quotes.info.confirmDeleteQuote', { name: quote.displayName })} />

            {/* Delete item */}
            <ConfirmModal open={confirmDeleteItemOpen} onClose={() => setConfirmDeleteItemOpen(false)}
                onConfirm={handleDeleteItem} isLoading={submitting}
                question={t('quotes.info.confirmDeleteItem')} />

            {/* Mark as sent */}
            <ConfirmModal open={confirmSentOpen} onClose={() => setConfirmSentOpen(false)}
                onConfirm={handleMarkSent} isLoading={submitting}
                question={t('quotes.info.confirmMarkSent')} />

            {/* Accept */}
            <ConfirmModal open={confirmAcceptOpen} onClose={() => setConfirmAcceptOpen(false)}
                onConfirm={handleAccept} isLoading={submitting}
                question={t('quotes.info.confirmAccept')} />

            {/* Reject */}
            <ConfirmModal open={confirmRejectOpen} onClose={() => setConfirmRejectOpen(false)}
                onConfirm={handleReject} isLoading={submitting}
                question={t('quotes.info.confirmReject')} />

            {/* Cancel */}
            <ConfirmModal open={confirmCancelOpen} onClose={() => setConfirmCancelOpen(false)}
                onConfirm={handleCancel} isLoading={submitting}
                question={
                    ['COMPANY_SIGNED', 'AWAITING_CUSTOMER_SIGNATURE'].includes(quote?.status)
                        ? t('quotes.info.confirmCancelWithSigning')
                        : t('quotes.info.confirmCancel')
                }
            />

            {/* Delete note */}
            <ConfirmModal open={confirmDeleteNoteOpen} onClose={() => setConfirmDeleteNoteOpen(false)}
                onConfirm={handleDeleteNote} isLoading={noteSubmitting}
                question={t('quotes.info.confirmDeleteNote')} />

            {/* AI contract generation */}
            <GenerateContractModal
                open={generatingContract}
                onClose={() => setGeneratingContract(false)}
                quoteId={id}
                mode="generate"
                onDone={handleContractGenerated}
            />

            {/* PDF preview */}
            <Dialog
                open={pdfPreviewOpen}
                onClose={closePdfPreview}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { height: '92vh', borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, px: 2.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{quote.displayName}</Typography>
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t('quotes.info.pdfDownload')}>
                            <IconButton size="small" onClick={downloadPdf}>
                                <DownloadIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.close')}>
                            <IconButton size="small" onClick={closePdfPreview}>
                                <CloseIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ flex: 1, p: 0, overflow: 'hidden' }}>
                    {pdfLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress sx={{ color: '#427898' }} />
                        </Box>
                    ) : pdfBlobUrl ? (
                        <iframe
                            src={pdfBlobUrl}
                            title="PDF Preview"
                            style={{ width: '100%', height: '100%', border: 'none' }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
